import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/dist/sweetalert2.css';
import {randomBeta} from 'd3-random';
import './styles/common.scss';
import './styles/pop-up-modal.scss';
import './styles/slide-in-box.scss';
import './styles/banner.scss';
import './styles/inline.scss';
import './styles/preview-modals.scss';

import {magnetIsAllowedByPatterns} from './rule';
import feature from './feature';
import {scopeCustomCss} from './customCss';
import countryCodes from './countryCodes';
import statesUS from './statesUS';
import provincesCanada from './provincesCanada';
import {isExpired, setExpirationAfter} from './time';
import {validateEmail} from "./validation";

const loaders = [];
let lock = false;
let liveEngagement = null;
let liveEngagementDelayTimer = null;

// backward compatible keys
const LAST_SHOWN_MAGNET_TIMESTAMP = 'LAST_SHOWN_MAGNET_TIMESTAMP';
const NUM_MAGNETS_SHOWN = 'NUM_MAGNETS_SHOWN';
const MAGNET_SETTINGS_EXPIRATION = 'MAGNET_SETTINGS_EXPIRATION';
const oldFormsPermanentlyDismissedKey = 'magnet-forms-permanently-dismissed';
const oldFormsConfirmedKey = 'magnet-forms-confirmed';

// new multi-stage keys
const multiStageFormsPermanentlyDismissedKey = 'le-permanently-dismissed';
const multiStageFormsConfirmedPrefix = 'le-confirmed-';
const LIVE_ENGAGEMENT_SHOWN_TIMESTAMPS = 'LIVE_ENGAGEMENT_SHOWN_TIMESTAMPS';

const flowControlRegex = /^(\d+)([hmd])$/;

export default class LiveEngagement {
   /**
    * @param {Tracker} tracker
    */
   constructor(tracker) {
      if (!tracker) {
         return console.error('Tracker not provided.');
      }

      this.tracker = tracker;
      liveEngagement = null;
      if (liveEngagementDelayTimer) {
         clearTimeout(liveEngagementDelayTimer);
         liveEngagementDelayTimer = null;
      }
      this.run();
   }

   async run() {
      this.queueLiveEngagementLoading(this.tracker);

      pubsub.subscribe('shown', data => {
         this.tracker.api.sendEvent('lead-magnet-shown', data);
      });
      pubsub.subscribe('confirmed', async data => {
         this.setConfirmedForm(data);
         let primaryEmail = data.email;
         if (this.tracker.featureFlags[feature.Sc17569]) {
            primaryEmail = findPrimaryEmail(data, this.tracker);
         }

         if (primaryEmail) {
            await this.tracker.identify(primaryEmail, {
               identified_by: 'campaign',
               identifying_campaign: data.form_id,
               identifying_campaign_stage: data.stage_id,
               identifying_campaign_touchpoint: data.touchpoint_id,
               identifying_campaign_variant: data.variant_id,
            });
         }
         await this.tracker.api.sendEvent('lead-magnet-confirmed', data);
         const redirectUrl = this.getRedirectUrl(liveEngagement, data);
         if (redirectUrl) {
            if (liveEngagement.form_data.open_button_link_in_new_tab) {
               window.open(redirectUrl, '_blank');
            } else {
               window.location.href = redirectUrl;
            }
         } else {
            Swal.fire('Thank You!', '', 'success');
         }
      });
      pubsub.subscribe('cancelled', data => {
         this.tracker.api.sendEvent('lead-magnet-cancelled', data);
      });
      pubsub.subscribe('dismiss', data => {
         this.appendPermanentlyDismissed(data);
         this.tracker.api.sendEvent('lead-magnet-permanently-dismissed', data);
      });
   }

   /**
    * @param {Tracker} tracker
    */
   async queueLiveEngagementLoading(tracker) {
      loaders.push({
         loader: (tracker) => this.loadLiveEngagement(tracker),
         tracker: tracker
      });

      if (!lock) {
         while (loaders.length && !liveEngagement) {
            let {loader, tracker} = loaders.pop();
            await loader(tracker);
         }
         lock = false;
      }
   }

   async loadLiveEngagement(tracker) {
      lock = true;
      let res = this.getLiveEngagementCampaign(tracker);
      if (!res) {
         return;
      }

      const delay = (magnetShowFunc) => {
         liveEngagementDelayTimer = setTimeout(magnetShowFunc, this.getDelay(liveEngagement));
      };

      if (this.isMobileDevice() && res?.form_data?.hide_on_mobile) {
         return;
      }

      if (res.form_data && Object.keys(res.form_data).length !== 0) {
         liveEngagement = res;
         if (liveEngagement.form_data.prompt_type !== 'inline') {
            delay(this.getLiveEngagementShowFunc(tracker, liveEngagement));
         } else {
            this.getLiveEngagementShowFunc(tracker, liveEngagement)();
         }
      }
   }

   getLiveEngagementCampaign(tracker) {
      const {campaigns} = tracker.campaigns;

      if (Array.isArray(campaigns)) {
         const liveEngagements = campaigns.filter(c => {
            return this.isCampaignActive(c)
               && !this.isOldFormPermanentlyDismissed(c.code) // backward compatibility
               && !this.isOldFormAlreadyConfirmed(c.code) // backward compatibility
               && !this.isMultiStageFormPermanentlyDismissed(c.code);
         })

         const visitorCampaignStages = this.getVisitorCampaignStagesToShow(tracker, liveEngagements);

         if (Object.keys(visitorCampaignStages).length > 0) {
            return this.chooseBestLiveEngagementVariant(tracker, liveEngagements, visitorCampaignStages);
         }
      }

      return null;
   }

   getVisitorCampaignStagesToShow(tracker, liveEngagements) {
      let visitorCampaignStages = {};

      for (const le of liveEngagements) {
         let stage;
         const prevStage = this.findPreviouslyConfirmedStage(tracker, le.code);

         if (prevStage) {
            stage = this.getNextStageToShow(tracker, le, prevStage);
         } else {
            stage = this.findFirstStageToShow(tracker, le);
         }

         if (stage) {
            visitorCampaignStages[le.code] = stage;
         }
      }

      return visitorCampaignStages;
   }

   // return {id: "stage-id", ts: {timestamp}}
   findPreviouslyConfirmedStage(tracker, code) {
      // check local storage data
      const stage = localStorage.getItem(`${multiStageFormsConfirmedPrefix}${code}`);
      if (stage) {
         return JSON.parse(stage);
      }

      // check backend data
      const interaction = tracker.personalizationData.campaignInteraction?.find(i => i.code === code);
      if (interaction) {
         return {id: interaction.stage_id, ts: new Date(interaction.date).getTime()}
      }

      return null;
   }

   getNextStageToShow(tracker, campaign, previouslyConfirmedStage) {
      const previousIndex = campaign.stages.findIndex(stage => stage.id === previouslyConfirmedStage.id);
      if (previousIndex === campaign.stages.length - 1) {
         // Return if it was the last stage
         return "";
      }

      // side loaded stages have priority, start always from them
      let sideLoadedIndex;
      const sideLoadedStage = this.findPossibleSideLoadedStage(tracker, campaign);
      if (sideLoadedStage) {
         sideLoadedIndex = campaign.stages.findIndex(stage => stage.id === sideLoadedStage.id);
      }

      // check if the side loaded stage was found and is after the already confirmed stage
      if (sideLoadedStage && sideLoadedIndex > previousIndex) {
         // side loaded stage should be processed instead of the previously confirmed stage
         if (this.stageHasInlineVariant(sideLoadedStage) || !this.isMultiStageFormInsideDisplayFrequencyWindow(campaign, sideLoadedStage)) {
            return sideLoadedStage.id;
         }

         // show it later
         return "";
      }

      if (previousIndex === -1) {
         return "";
      }

      const nextStage = campaign.stages[previousIndex + 1];
      if (!this.stageHasInlineVariant(nextStage) && this.isMultiStageFormInsideDisplayFrequencyWindow(campaign, nextStage)) {
         return "";
      }

      if (!this.isFormAllowedByFlowControl(nextStage, previouslyConfirmedStage.ts)) {
         return "";
      }

      return nextStage.id;
   }

   findFirstStageToShow(tracker, campaign) {
      // side loaded stages have priority, start always from them
      const sideLoadedStage = this.findPossibleSideLoadedStage(tracker, campaign);
      if (sideLoadedStage) {
         // side loaded stage was found, but we still need to check if we can actually show it
         if (this.stageHasInlineVariant(sideLoadedStage) || !this.isMultiStageFormInsideDisplayFrequencyWindow(campaign, sideLoadedStage)) {
            return sideLoadedStage.id;
         }

         // show it later
         return "";
      }

      // side load segment not found, check first stage
      const stage = campaign.stages[0];
      if (!stage.segments?.length) {
         if (this.stageHasInlineVariant(stage)
             || (!this.isOldFormInsideDisplayFrequencyWindow(campaign, stage) // backward compatibility
             && !this.isMultiStageFormInsideDisplayFrequencyWindow(campaign, stage))) {
            return stage.id
         }
      }

      if (stage.segments?.some(seg => tracker.personalizationData.segments?.includes(seg))) {
         if (this.stageHasInlineVariant(stage)
            || (!this.isOldFormInsideDisplayFrequencyWindow(campaign, stage) // backward compatibility
            && !this.isMultiStageFormInsideDisplayFrequencyWindow(campaign, stage))) {
            return stage.id
         }
      }

      return "";
   }

   stageHasInlineVariant(stage) {
      return stage.touchpoints?.some(touchpoint =>
         touchpoint.variants?.some(variant => variant.form_data?.prompt_type === 'inline')
      );
   }

   findPossibleSideLoadedStage(tracker, campaign) {
      // reverse the stages array to start from the latest stage and filter by side loaded segment
      for (let i = campaign.stages?.length - 1; i >= 1; i--) {
         const stage = campaign.stages[i];
         if (stage.segments?.length > 0) {
            if (tracker.personalizationData.segments?.includes(stage.segments[0])) {
               return stage;
            }
         }
      }

      return null;
   }

   chooseBestLiveEngagementVariant(tracker, liveEngagements, visitorCampaignStages) {
      let bestScore = -1;
      let chosenVariant;
      let chosenTouchpoint;
      let stageToShow;
      let chosenCampaign;

      for (const campaign of liveEngagements) {
         const { variant, touchpoint, stage, score } = this.getMABVariantForCampaign(tracker, campaign, visitorCampaignStages);
         if (variant && (score > bestScore)) {
            chosenVariant = variant;
            chosenTouchpoint = touchpoint;
            stageToShow = stage;
            chosenCampaign = JSON.parse(JSON.stringify(campaign));
            bestScore = score;
         }
      }

      if (!chosenVariant) {
         return null;
      }

      return this.applyFormToCampaign(chosenCampaign, stageToShow, chosenTouchpoint, chosenVariant);
   }

   getMABVariantForCampaign(tracker, campaign, visitorCampaignStages) {
      const m = campaign.stages.length;
      const n = this.findStageNumber(campaign, visitorCampaignStages);

      if (n !== -1) {
         const stage = campaign.stages[n-1];
         const { variant, touchpoint, score } = this.getMABVariantForStage(tracker, stage);

         if (variant) {
            return {
               variant,
               touchpoint,
               stage,
               score: score * this.getCampaignProgressMultiplier(m, n) * this.getCampaignPriorityWeight(campaign)
            };
         }
      }

      return { variant: null, touchpoint: null, stage: null, score: -1 };
   }

   findStageNumber(campaign, visitorCampaigns) {
      const campaignCode = campaign.code;
      const stageId = visitorCampaigns[campaignCode];

      if (!stageId) {
         return -1;
      }

      const stageIndex = campaign.stages.findIndex(stage => stage.id === stageId);

      return stageIndex !== -1 ? stageIndex + 1 : -1;
   }

   /**
    * `m` - Total number of stages in the campaign
    * `n` - Current stage number
    */
   getCampaignProgressMultiplier(m, n) {
      const finalStageMultiplier = this.getFinalStageMultiplier(m, n);

      return (Math.log(m + 2) - 0.08 * (4 - n)) / (m - n + 1) * finalStageMultiplier;
   }

   // If the customer is on the final stage, calculate the multiplier, else return 1
   getFinalStageMultiplier(m, n) {
      if (m === n) {
         return Math.pow(0.9, m);
      }
      return 1;
   }

   getCampaignPriorityWeight(campaign) {
      return campaign.priority || 1;
   }

   getMABVariantForStage(tracker, stage) {
      let bestVariant;
      let bestScore = -1;
      let bestTouchpoint;

      // select the best touchpoint
      for (const touchpoint of stage.touchpoints) {
         if (this.isTouchpointValid(tracker, touchpoint)) {
            const { variant, score } = this.getMABVariantForTouchpoint(touchpoint);
            if (variant && (score > bestScore)) {
               bestVariant = variant;
               bestTouchpoint = touchpoint;
               bestScore = score;
            }
         }
      }

      return { variant: bestVariant, touchpoint: bestTouchpoint, score: bestScore };
   }

   getMABVariantForTouchpoint(touchpoint) {
      let bestScore = -1;
      let bestVariant;

      // select the best variant
      for (const variant of touchpoint.variants) {
         if (this.isVariantValid(variant)) {
            const impressions = variant?.impressions || 0;
            const conversions = variant?.conversions || 0;

            let variantDistribution = randomBeta(conversions + 1, impressions - conversions + 1);
            const score = variantDistribution();
            if (score > bestScore) {
               bestVariant = variant;
               bestScore = score;
            }
         }
      }

      return { variant: bestVariant, score: bestScore };
   }

   applyFormToCampaign(campaign, stage, touchpoint, variant) {
      campaign.form_data = variant.form_data;
      campaign.goal_url = variant.goal_url;
      campaign.custom_css = variant.custom_css;
      campaign.lead_gen_form = variant.type === 'lead_gen_form';
      campaign.active_from = variant.active_from;
      campaign.active_to = variant.active_to;
      campaign.chosen_best_variant_id = variant.variant_id;
      campaign.stage = stage;
      campaign.display_frequency = stage.display_frequency;
      campaign.touchpoint = touchpoint;

      return campaign;
   }

   /**
    * @param tracker
    * @param {Object} liveEngagement  - liveEngagement campaign object
    * @returns {(function(): Promise<void>)|*}
    */
   getLiveEngagementShowFunc(tracker, liveEngagement) {
      liveEngagement = convertToSnakeCase(liveEngagement);

      const conversionTrackingEnabled = tracker.featureFlags[feature.Sc27297];
      const customCssEnabled = tracker.featureFlags[feature.Sc29425];

      let data = {}
      if (tracker.featureFlags[feature.Sc16728]) {
         data = {
            form_id: liveEngagement.code,
            variant_id: liveEngagement?.chosen_best_variant_id,
            campaign_code: liveEngagement.code,
         };
      } else {
         data = {
            form_id: liveEngagement.code,
            campaign_code: liveEngagement.code,
         };
      }
      if (liveEngagement?.stage?.id) {
         data.stage_id = liveEngagement.stage.id;
      }
      if (liveEngagement?.touchpoint?.id) {
         data.touchpoint_id = liveEngagement.touchpoint.id;
      }
      let hideModal = false;

      return async () => {
         if (document.visibilityState !== 'visible') {
            return;
         }

         let popupIsModal = true;
         if (liveEngagement.form_data.prompt_type && liveEngagement.form_data.prompt_type !== 'modal') {
            popupIsModal = false;
         }

         if (!tracker.arePopupsAllowed() && popupIsModal) {
            return;
         }

         const primaryInstitutionName = tracker.campaigns.primary_institution || tracker.campaigns.primaryInstitutionName || '';

         if (liveEngagement.form_data.prompt_type !== 'inline') {
            if (!this.canShowMagnetCampaignBasedOnGlobalSettings(tracker)) return;
            if (!this.canShowVariantBasedOnFatigueSettings(liveEngagement)) return;
         }

         if (!this.canShowMagnet(liveEngagement)) {
            return;
         }

         const magnetWrap = document.createElement('div');
         magnetWrap.classList.add('swal-wrapper');
         magnetWrap.classList.add('hum-' + (liveEngagement.form_data.prompt_type ?? 'modal'));
         let customCssStyleEl = null;
         if (customCssEnabled && typeof liveEngagement.custom_css === 'string' && liveEngagement.custom_css.trim() !== '') {
            magnetWrap.classList.add('hum-style-root');
            customCssStyleEl = injectCustomCss(magnetWrap, liveEngagement.custom_css);
         }

         let options = await prepareSwalOptions(tracker, data, liveEngagement.form_data, magnetWrap);

         if (!options) {
            return;
         }

         this.setRepeatTs(liveEngagement);
         pubsub.publish('shown', data);

         const shouldUseCustomFormFields = tracker.featureFlags[feature.Sc17569];
         const shouldUseNewEmailValidation = tracker.featureFlags[feature.Sc27746];

         if (liveEngagement.form_data.prompt_type !== 'inline') {
            this.updateLiveEngagementsShownAtTimestamps();
         }

         await Swal.fire({
            html: setUpMagnetBody(liveEngagement, primaryInstitutionName, tracker),
            preConfirm: magnetPreConfirm(data, shouldUseNewEmailValidation),
            willClose: () => hideModal = checkHideModal(),
            didRender: (doc) => magnetDidRender(doc, liveEngagement.form_data, false, tracker.settings, conversionTrackingEnabled),
            didClose: () => {
               if (customCssStyleEl) {
                  customCssStyleEl.remove();
               }
            },
            didOpen: () => {
               Swal.getPopup().id = liveEngagement.code;
               if (shouldUseCustomFormFields) {
                  const countrySelectContainers = document.querySelectorAll('.country-select-container');
                  countrySelectContainers.forEach(container => {
                     const matchingStateContainer = findMatchingStateContainer(container);
                     if (matchingStateContainer) {
                        addListenerToCountrySelect(container, matchingStateContainer);
                     }
                  });
               }
            },
            showConfirmButton: this.shouldShowConfirmButton(liveEngagement, conversionTrackingEnabled),
            confirmButtonText: liveEngagement.form_data.submit_label || 'Submit',
            showCancelButton: liveEngagement.form_data.cancel_toggle === undefined ? true : liveEngagement.form_data.cancel_toggle,
            cancelButtonText: 'Cancel',
            showCloseButton: true,
            footer: setUpMagnetFooter(liveEngagement, primaryInstitutionName),
            allowOutsideClick: true,
            ...options,
         }).then((result) => {
            if (result.isConfirmed) {
               if (hideModal) {
                  this.appendPermanentlyDismissed(data);
               }

               if (result.targetUrl) {
                  data.target_url = result.targetUrl;
               }

               pubsub.publish('confirmed', data);
               this.removeRepeatTs(liveEngagement);
            } else if (result.isDenied || result.isDismissed) {
               if (hideModal) {
                  pubsub.publish('dismiss', data);
                  return;
               }

               pubsub.publish('cancelled', data);
            }
         });
      };
   }

   shouldShowConfirmButton(liveEngagement, conversationTrackingEnabled) {
      if (!conversationTrackingEnabled) {
         //if the feature flag is off, just use the old logic. Delete this whole if block when cleaning up the feature flag
         return liveEngagement.lead_gen_form || liveEngagement.form_data.call_to_action_toggle ? true : liveEngagement.form_data.call_to_action_toggle;
      }

      if (liveEngagement?.lead_gen_form) {
         let hideSubmit = liveEngagement?.form_data?.hide_submit ?? false;
         return !hideSubmit
      }

      return liveEngagement?.form_data?.call_to_action_toggle;
   }

   getRedirectUrl(liveEngagement, data) {
      if (data.target_url) {
         return data.target_url;
      }

      const submitLink = liveEngagement?.form_data?.submit_link;
      if (submitLink && submitLink.trim() !== '') {
         return submitLink;
      }
      return liveEngagement?.goal_url;
   }

   getDurationTime(duration) {
      const match = duration.match(flowControlRegex);
      if (!match) {
         return null;
      }

      const value = parseInt(match[1], 10);
      const unit = match[2];

      let timeInMillis;
      switch (unit) {
         case 'h':
            timeInMillis = value * 60 * 60 * 1000;
            break;
         case 'm':
            timeInMillis = value * 60 * 1000;
            break;
         case 'd':
            timeInMillis = value * 24 * 60 * 60 * 1000;
            break;
      }

      return timeInMillis;
   }

   getFrequencyFactor(timeframeType) {
      let timeFrame = this.getTimeFactor(timeframeType);
      // multi-stage campaign settings have 5 minutes as a hardcoded value
      if (timeframeType === 'minutes') {
         timeFrame *= 5
      }

      return timeFrame;
   }

   getTimeFactor(timeframeType) {
      switch (timeframeType) {
         case 'second':
         case 'seconds':
            return 1000;
         case 'minute':
         case 'minutes':
            return 60 * 1000;
         case 'hour':
         case 'hours':
            return 60 * 60 * 1000;
         case 'day':
         case 'days':
            return 24 * 60 * 60 * 1000;
         case 'week':
         case 'weeks':
            return 7 * 24 * 60 * 60 * 1000;
         default:
            return 1000;
      }
   }

   setConfirmedForm(data) {
      const key = `${multiStageFormsConfirmedPrefix}${data.form_id}`;

      localStorage.setItem(key, JSON.stringify({
         ts: Date.now(),
         id: data.stage_id,
      }));
   }

   appendPermanentlyDismissed(data) {
      const mf = localStorage.getItem(multiStageFormsPermanentlyDismissedKey);
      if (!mf) {
         localStorage.setItem(multiStageFormsPermanentlyDismissedKey, JSON.stringify([data.form_id]));
         return;
      }
      const mfArr = JSON.parse(mf);
      if (!mfArr.includes(data.form_id)) {
         localStorage.setItem(multiStageFormsPermanentlyDismissedKey, JSON.stringify([data.form_id, ...mfArr]));
      }
   }

   magnetTsKey(magnetCode) {
      return `ts-${magnetCode}`;
   }

   liveEngagementStartStageKey(leCode) {
      return `le-start-${leCode}`;
   }

   liveEngagementRepeatStageKey(leCode) {
      return `le-repeat-${leCode}`;
   }

   /**
    * Returns number of milliseconds to delay campaign showing for.
    *
    * @param {Object} campaign
    * @return {Number} number of milliseconds
    */
   getDelay(campaign) {
      const campaignDelay = campaign?.form_data?.delay || 3;
      const repeatFactor = this.getTimeFactor(campaign?.display_frequency?.type);
      const repeatDelay = repeatFactor * (campaign?.display_frequency?.value || campaignDelay);
      let delaySettingsType = 'start';
      let delaySettingsTS = Date.now();

      const key = this.magnetTsKey(campaign.code); // backward compatibility check first
      const data = localStorage.getItem(key);
      if (data) {
         // old magnet key found
         const delaySettings = JSON.parse(data);
         delaySettingsType = delaySettings.type;
         delaySettingsTS = delaySettings.ts;
      } else {
         const startKey = this.liveEngagementStartStageKey(campaign.code);
         const startData = localStorage.getItem(startKey);

         if (!startData) {
            const repeatKey = this.liveEngagementRepeatStageKey(campaign.code);
            const repeatData = localStorage.getItem(repeatKey);

            if (!repeatData) {
               localStorage.setItem(startKey, JSON.stringify({id: campaign.stage.id, ts: delaySettingsTS}));
            } else {
               const delaySettings = JSON.parse(repeatData);
               delaySettingsType = delaySettings.type;
               delaySettingsTS = delaySettings.ts;
            }
         }
      }

      const delay = (delaySettingsType === 'start' ? campaignDelay : repeatDelay);

      return Math.max(delaySettingsTS + delay - Date.now(), campaignDelay * 1000);
   }

   setRepeatTs(liveEngagement) {
      localStorage.removeItem(this.liveEngagementStartStageKey(liveEngagement.code));
      localStorage.setItem(this.liveEngagementRepeatStageKey(liveEngagement.code), JSON.stringify({
         ts: Date.now(),
         id: liveEngagement.stage.id
      }));
   }

   removeRepeatTs(liveEngagement) {
      localStorage.removeItem(this.liveEngagementRepeatStageKey(liveEngagement.code));
   }


   // validation section
   isCampaignActive(campaign) {
      const currentDate = new Date();
      return campaign.is_active && currentDate >= new Date(campaign.active_from) && currentDate <= new Date(campaign.active_to);
   }

   isOldFormPermanentlyDismissed(formId) {
      return this.isFormInStorage(formId, oldFormsPermanentlyDismissedKey);
   }

   isMultiStageFormPermanentlyDismissed(formId) {
      return this.isFormInStorage(formId, multiStageFormsPermanentlyDismissedKey);
   }

   isOldFormAlreadyConfirmed(formId) {
      return this.isFormInStorage(formId, oldFormsConfirmedKey);
   }

   // check if form "abc" exists in ["foo", "bar"]
   isFormInStorage(formId, storageKey) {
      const mf = localStorage.getItem(storageKey);
      if (!mf) {
         return false;
      }
      const mfArr = JSON.parse(mf);
      return !!mfArr.includes(formId);
   }

   isOldFormInsideDisplayFrequencyWindow(campaign, stage) {
      let key = this.magnetTsKey(campaign.code);
      return this.isFormInsideDisplayFrequencyWindow(stage, key);
   }

   isMultiStageFormInsideDisplayFrequencyWindow(campaign, stage) {
      let key = this.liveEngagementRepeatStageKey(campaign.code);
      return this.isFormInsideDisplayFrequencyWindow(stage, key);
   }

   isFormInsideDisplayFrequencyWindow(stage, key) {
      let data = localStorage.getItem(key);

      if (!data) {
         return false;
      }

      let campaignHistory = JSON.parse(data);

      if (stage.id !== campaignHistory.id) {
         return false;
      }

      const repeatFactor = this.getTimeFactor(stage.display_frequency?.type);
      const repeatDelay = repeatFactor * (stage.display_frequency?.value);

      return Date.now() < campaignHistory.ts + repeatDelay;
   }

   isFormAllowedByFlowControl(nextStage, previousTSInteraction) {
      if (!nextStage.delay_settings?.type) {
         return true;
      }

      const currentTime = new Date().getTime();
      switch (nextStage.delay_settings?.type) {
         case 'duration': {
            const duration = this.getDurationTime(nextStage.delay_settings.duration);
            if (!duration) {
               return false;
            }
            return currentTime - previousTSInteraction > duration;
         }
         case 'until': {
            return currentTime > new Date(nextStage.delay_settings.until).getTime();
         }
         default: {
            return false;
         }
      }
   }

   isTouchpointValid(tracker, touchpoint) {
      // status validation
      if (touchpoint.status !== 'active') {
         return false;
      }

      // segment validation
      if (touchpoint.segments?.length > 0) {
         if (!touchpoint.segments.some(segment => tracker.personalizationData.segments?.includes(segment))) {
            return false;
         }
      }

      // pages validation
      const globallyExcludedPages = tracker.settings?.leadGenCampaigns?.excludedPages;
      return magnetIsAllowedByPatterns(window.location.href, touchpoint.url_patterns, touchpoint.excluded_urls, globallyExcludedPages);
   }

   isVariantValid(variant) {
      const currentDate = new Date();

      if (variant.active_from) {
         const fromDate = new Date(variant.active_from);
         if (fromDate.getTime() > 0 && currentDate <= fromDate) {
            return false;
         }
      }

      if (variant.active_to) {
         const toDate = new Date(variant.active_to);
         if (toDate.getTime() > 0 && currentDate >= toDate) {
            return false;
         }
      }

      return true;
   }

   // Check if we can show magnet campaigns based on global settings
   // Example: Display a max of 2 campaigns within 1 hour
   canShowMagnetCampaignBasedOnGlobalSettings(tracker) {
      this.cleanLiveEngagementShownTimestamps(tracker);

      if (this.validateMultistageCampaignEligibility(tracker)) {
         return this.checkCampaignInteractionWithinTimeframe(tracker);
      }
   }

   validateMultistageCampaignEligibility(tracker) {
      const timelinePrompts = tracker.settings?.leadGenCampaigns?.timelinePrompts || [];
      const rawTimestamps = localStorage.getItem(LIVE_ENGAGEMENT_SHOWN_TIMESTAMPS);
      const timestamps = rawTimestamps ? JSON.parse(rawTimestamps) : [];
      const now = Date.now();

      for (const prompt of timelinePrompts) {
         if (!prompt.enabled) continue;

         const limit = prompt.value;
         const timeFrame = this.getFrequencyFactor(prompt.label.toLowerCase());

         const interactionsWithinFrame = timestamps.filter(
            (timestamp) => now - timestamp <= timeFrame
         );

         if (interactionsWithinFrame.length >= limit) {
            return false;
         }
      }


      return true;
   }

   // Check if we can show magnet campaigns based on global settings
   // Example: Display a max of 2 campaigns within 1 hour
   validateMagnetCampaignEligibility(tracker) {
      let maxCampaignsLimit = tracker.settings?.leadGenCampaigns?.maxCampaigns || 2;
      let timeframeLimit = tracker.settings?.leadGenCampaigns?.timeframe || 24;
      let timeframeType = tracker.settings?.leadGenCampaigns?.timeframeType || 'hours';

      // reset all variables if the settings were expired
      if (isExpired(MAGNET_SETTINGS_EXPIRATION)) {
         setExpirationAfter(MAGNET_SETTINGS_EXPIRATION, timeframeLimit * this.getTimeFactor(timeframeType) / 1000);
         localStorage.setItem(LAST_SHOWN_MAGNET_TIMESTAMP, '0');
         localStorage.setItem(NUM_MAGNETS_SHOWN, '0');
      }

      let lastShownTimestamp = parseInt(localStorage.getItem(LAST_SHOWN_MAGNET_TIMESTAMP)) || 0;
      let numMagnetsShown = parseInt(localStorage.getItem(NUM_MAGNETS_SHOWN)) || 0;

      const currentTime = new Date().getTime();
      const timeDifference = currentTime - lastShownTimestamp;

      // first check number of already shown magnets
      const isnumMagnetsShownLimitReached = numMagnetsShown < maxCampaignsLimit;
      const antiFatigueTimeframePassed = timeDifference >= timeframeLimit * this.getTimeFactor(timeframeType)

      // if first check did not pass then check the time limitation
      return isnumMagnetsShownLimitReached || antiFatigueTimeframePassed
   }

   checkCampaignInteractionWithinTimeframe(tracker) {
      if (
         tracker.personalizationData?.interactedCampaigns &&
         tracker.personalizationData.interactedCampaigns.length > 0 &&
         tracker.personalizationData.interactedCampaigns.some(c => c !== null)
      ) {
         const currentTime = new Date().getTime();
         const timelinePrompts = tracker.settings?.leadGenCampaigns?.timelinePrompts || [];

         for (const prompt of timelinePrompts) {
            if (!prompt.enabled) continue;

            const limit = prompt.value;
            const timeFrame = this.getFrequencyFactor(prompt.label.toLowerCase());

            const interactedCampaigns = tracker.personalizationData.interactedCampaigns.filter(campaign => {
               const interactionTime = new Date(campaign.date).getTime();
               return currentTime - interactionTime <= timeFrame;
            });

            if (interactedCampaigns.length >= limit) {
               return false;
            }
         }
      }

      return true; //If there were no interactions
   }

   cleanLiveEngagementShownTimestamps(tracker) {
      const rawTimestamps = localStorage.getItem(LIVE_ENGAGEMENT_SHOWN_TIMESTAMPS);
      const timestamps = rawTimestamps ? JSON.parse(rawTimestamps) : [];
      if (!timestamps.length) {
         return;
      }

      const timelinePrompts = tracker.settings?.leadGenCampaigns?.timelinePrompts || [];
      const maxEnabledTimeframe = Math.max(
         ...timelinePrompts
            .filter((prompt) => prompt.enabled)
            .map((prompt) => {
               return this.getFrequencyFactor(prompt.label.toLowerCase());
            })
      );

      const cutoffTimestamp = Date.now() - maxEnabledTimeframe;
      const cleanedTimestamps = timestamps.filter((timestamp) => timestamp >= cutoffTimestamp);

      localStorage.setItem(LIVE_ENGAGEMENT_SHOWN_TIMESTAMPS, JSON.stringify(cleanedTimestamps));
   }

   updateLiveEngagementsShownAtTimestamps() {
      const rawTimestamps = localStorage.getItem(LIVE_ENGAGEMENT_SHOWN_TIMESTAMPS);
      const timestamps = rawTimestamps ? JSON.parse(rawTimestamps) : [];

      timestamps.push(Date.now());
      localStorage.setItem(LIVE_ENGAGEMENT_SHOWN_TIMESTAMPS, JSON.stringify(timestamps));
   }

   canShowVariantBasedOnFatigueSettings(magnet) {
      return !this.isOldFormInsideDisplayFrequencyWindow(magnet, magnet.stage) // backward compatibility
         && !this.isMultiStageFormInsideDisplayFrequencyWindow(magnet, magnet.stage);
   }

   canShowMagnet(magnet) {
      return !this.isOldFormAlreadyConfirmed(magnet.code) // backward compatibility
         && !this.isOldFormPermanentlyDismissed(magnet.code) // backward compatibility
         && !this.isMultiStageFormPermanentlyDismissed(magnet.code);
   }

   isMobileDevice() {
      return /Mobile/i.test(navigator.userAgent) || window.innerWidth <= 768;
   }
}

// Pubsub class copied from liveEngagementPreview.js without any changes
// TODO: refactor in scope of 21384
const pubsub = new class {
   constructor() {
      this.events = {};
   }

   subscribe(event, handler) {
      if (this.events[event]) {
         this.events[event].push(handler);
      } else {
         this.events[event] = [handler];
      }
   }

   publish(event, ...args) {
      const handlers = this.events[event];
      if (Array.isArray(handlers)) {
         handlers.forEach((handler) => {
            handler(...args);
         });
      }
   }
};

// The functions below were copied from liveEngagementPreview.js without any changes
// They are responsible for the modal rendering
// TODO: refactor in scope of 21384
async function prepareSwalOptions(tracker, data, form_data, wrap) {
   let options;
   switch (form_data.prompt_type) {
      case 'inline':
         options = await prepareInlineWidgetOptions(data, form_data.prompt_css_selector, wrap, tracker.featureFlags[feature.Sc17569]);
         break;
      case 'slide_in_box':
         options = await prepareSlideInBoxWidgetOptions(data, form_data.prompt_position, wrap, tracker.featureFlags[feature.Sc17569]);
         break;
      case 'banner':
         options = await prepareBannerWidgetOptions(data, form_data.prompt_position, wrap);
         break;
      default:
         options = await prepareModalWidgetOptions(data, wrap);
   }

   if (!options) {
      return null;
   }

   options = await preparePromptActionPositionOptions(options, form_data.actions_position);

   return options;
}

async function preparePromptActionPositionOptions(options, position) {
   if (position?.length === 0) {
      return options;
   }
   const actions = `swal-actions-${position}`;
   options.customClass = {
      actions,
      ...options.customClass,
   };
   return options;
}

async function prepareModalWidgetOptions(data, wrap) {
   const target = document.body;
   if (target) {
      target.appendChild(wrap);
   }
   return {
      target: wrap,
   };
}

async function prepareInlineWidgetOptions(data, cssSelector, wrap, shouldUseCustomFormFields) {
   const target = document.querySelector(cssSelector);
   if (!target) {
      console.warn(`[hum] Inline campaign selector not found: "${cssSelector}"`);
      return null;
   }
   target.appendChild(wrap);
   return {
      target: wrap,
      template: '#my-template',
      toast: true,
      width: '100%',
      timer: 10e10,
      timerProgressBar: false,
      position: 'bottom',
      showClass: {
         popup: 'animate__fadeIn',
         backdrop: 'swal2-backdrop-hide',
      },
      showCloseButton: false,
      didOpen: () => {
         const popup = document.getElementsByClassName('swal2-toast')[0];
         popup.classList.remove('swal2-toast');
         popup.id = data.form_id;
         popup.removeAttribute('role');
         if (shouldUseCustomFormFields) {
            const countrySelectContainers = document.querySelectorAll('.country-select-container');
            countrySelectContainers.forEach(container => {
               const matchingStateContainer = findMatchingStateContainer(container);
               if (matchingStateContainer) {
                  addListenerToCountrySelect(container, matchingStateContainer);
               }
            });
         }
      },
   };
}

async function prepareSlideInBoxWidgetOptions(data, widgetPosition, wrap, shouldUseCustomFormFields) {
   const popupShowClass = widgetPosition.includes('start') ? 'animate__slideInLeft' : 'animate__slideInRight';
   const popupHideClass = widgetPosition.includes('start') ? 'animate__slideOutLeft' : 'animate__slideOutRight';

   const target = document.body;
   if (target) {
      target.appendChild(wrap);
   }
   return {
      target: wrap,
      template: '#my-template',
      toast: true,
      timer: 10e10,
      timerProgressBar: false,
      position: widgetPosition,
      showClass: {
         popup: `animate__animated ${popupShowClass}`,
      },
      hideClass: {
         popup: `animate__animated ${popupHideClass}`,
      },
      didOpen: () => {
            const popup = Swal.getPopup();
            popup.classList.remove('swal2-toast');
            popup.id = data.form_id;
            popup.role = 'dialog';
            popup.ariaLive = 'assertive';
            popup.ariaModal = 'true';

            // handle focus for the modal trying to repeat the logic from sweetalert
            // the library itself has a lot of restricted/unsupported features when toast type is enabled
            const focusableElements = popup.querySelectorAll(
               'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            let firstElement;
            let lastElement;
            let confirmButton;
            if (focusableElements.length > 0) {
               confirmButton = popup.querySelector('.swal2-confirm');
               firstElement = focusableElements[0];
               lastElement = focusableElements[focusableElements.length - 1];
            } else {
               firstElement = popup;
               lastElement = popup;
            }
            confirmButton ? confirmButton.focus() : firstElement.focus();

            // Trap focus inside the modal
            popup.addEventListener("keydown", function (event) {
               if (event.key === "Tab") {
                  if (event.shiftKey) {
                     if (document.activeElement === firstElement) {
                        event.preventDefault();
                        lastElement.focus();
                     }
                  } else {
                     if (document.activeElement === lastElement) {
                        event.preventDefault();
                        firstElement.focus();
                     }
                  }
               }
            });
         if (shouldUseCustomFormFields) {
            const countrySelectContainers = document.querySelectorAll('.country-select-container');
            countrySelectContainers.forEach(container => {
               const matchingStateContainer = findMatchingStateContainer(container);
               if (matchingStateContainer) {
                  addListenerToCountrySelect(container, matchingStateContainer);
               }
            });
         }
      },
   };
}

async function prepareBannerWidgetOptions(data, widgetPosition, wrap) {
   const popupShowClass = widgetPosition.includes('top') ? 'animate__slideInDown' : 'animate__slideInUp';
   const popupHideClass = widgetPosition.includes('top') ? 'animate__slideOutUp' : 'animate__slideOutDown';

   const target = document.body;
   if (target) {
      target.appendChild(wrap);
   }

   const lastFocusedElement = document.activeElement;

   return {
      target: wrap,
      template: '#my-template',
      position: widgetPosition,
      grow: 'row',
      backdrop: false,
      showClass: {
         popup: `animate__animated ${popupShowClass}`,
      },
      hideClass: {
         popup: `animate__animated ${popupHideClass}`,
      },
      didOpen: () => {
         const popup = Swal.getPopup();
         popup.id = data.form_id;
         popup.removeAttribute('role');
         popup.ariaLive = 'polite';
         if (document.activeElement && popup.contains(document.activeElement)) {
            document.activeElement.blur();
         }
         lastFocusedElement?.focus();

         const countrySelectContainers = document.querySelectorAll('.country-select-container');
         countrySelectContainers.forEach(container => {
            const matchingStateContainer = findMatchingStateContainer(container);
            if (matchingStateContainer) {
               addListenerToCountrySelect(container, matchingStateContainer);
            }
         });
      }
   };
}

function setUpMagnetBody(campaign, primaryInstitutionName, tracker) {
   let body = '';

   if (campaign.form_data.body_html) {
      const body_html = findAndReplacePrimaryInstitutionName(campaign.form_data.body_html, primaryInstitutionName);
      body += `${body_html}`;
   }

   if (campaign.form_data.inputs) {
      campaign.form_data.inputs = campaign.form_data.inputs.sort((a, b) => {
         if (Object.hasOwn(a, 'order') && Object.hasOwn(b, 'order')) {
            return a.order - b.order;
         } else {
            return 0;
         }
      });
      body += '<div class="swal-input-container"><form>';

      if (tracker.featureFlags[feature.Sc17569]) {
         for (const [key, value] of Object.entries(campaign.form_data.inputs)) {
            if (!value.checked) continue;

            body += '<div class="swal-input-row">';

            if (value.type) {
               body = addCustomField(body, key, value);
            } else {
               body = addDefaultField(body, key, value);
            }

            if (value.required && value.type !== 'checkbox') {
               // Hide the required span for states/provinces until needed.
               const hideClass = (value.type === 'location' && value.slug.endsWith('state')) ? 'swal-hidden' : '';
               body += `<span class="swal-span-required ${hideClass}">Required *</span>`;
            }
            body += '</div>';

         }
      } else {
         for (const [key, value] of Object.entries(campaign.form_data.inputs)) {
            if (!value.checked) continue;

            body += '<div class="swal-input-row">';

            body = addDefaultField(body, key, value);

            if (value.required) {
               body += '<span class="swal-span-required">Required *</span>';
            }
            body += '</div>';

         }
      }

      body += '</form></div>';
   }

   return body;
}

function setUpMagnetFooter(campaign, primaryInstitutionName) {
   let footer = '<div>';

   if (campaign.form_data.footer_html) {
      const footer_html = findAndReplacePrimaryInstitutionName(campaign.form_data.footer_html, primaryInstitutionName);
      footer += `${footer_html}`;
   }

   if (campaign.form_data.hide_modal_display) {
      const hide_modal_text = campaign.form_data.hide_modal_text || "Don't show me this message again.";
      footer += `<div class="hide-modal-container">
            <input id="hide-modal-input" style="width: auto !important;" type="checkbox">&nbsp;&nbsp;
            <label for="hide-modal-input">${hide_modal_text}</label>
         </div>`;
   }
   footer += `</div>`;

   return footer;
}

function magnetPreConfirm(data, shouldUseNewEmailValidation) {
   return () => {
      let allRequiredFills = true;
      const elements = Array.from(document.querySelectorAll('[data-key]'));
      elements.forEach(el => {
         const docEl = document.getElementById(el.id);
         if (docEl) {
            docEl.style.border = '1px solid #d9d9d9';
            document.getElementById(`error-` + el.dataset.label)?.remove();

            const requireFailedCheck = el.dataset.required === 'true' && docEl.value === '';
            let emailFailedCheck = el.dataset.key === 'email' && !oldRegexValidateEmail(docEl.value);
            const countryFailedCheck = el.dataset.key === 'country' && docEl.value === 'Country';
            if (shouldUseNewEmailValidation) {
               if (el.attributes['type']?.value === 'email') {
                  document.getElementById('email-helper-error-' + el.dataset.label)?.remove();
               }

               emailFailedCheck = docEl.type === 'email' && docEl.value !== '' && !validateEmail(docEl.value);
               if (emailFailedCheck) {
                  const helper = document.createElement('div');
                  helper.id = 'email-helper-error-' + el.dataset.label;
                  helper.className = 'swal-span-required';
                  helper.textContent = 'Please enter a valid email address.';

                  docEl.insertAdjacentElement('afterend', helper);
               }
            }

            if (requireFailedCheck || emailFailedCheck || countryFailedCheck) {
               allRequiredFills = false;
               docEl.style.border = '1px solid red';
            }
         }
      });
      if (!allRequiredFills) {
         return false;
      }
      elements.forEach(el => {
         const docEl = document.getElementById(el.id);
         if (docEl && docEl.value) {
            switch (el.type) {
               case 'checkbox':
               case 'radio':
                  if (el.checked) {
                     data[el.dataset.key] = docEl.value;
                  }
                  break;
               default:
                  data[el.dataset.key] = docEl.value;
            }
         }
      });
   };
}

function checkHideModal() {
   return document.getElementById('hide-modal-input')?.checked ?? false;
}

function magnetDidRender(magnetDoc, data, previewMode = false, settings, conversionTrackingEnabled) {
   // .swal2-title should not be removed, only hidden
   const classesToSearch = [
      '.swal2-input', '.swal2-file', '.swal2-range', '.swal2-select', '.swal2-radio', '.swal2-checkbox',
      '.swal2-textarea', '.swal2-image', '.swal2-icon', '.swal2-progress-steps', '.swal2-cancel', '.swal2-deny',
      '.swal2-validation-message', '.swal2-timer-progress-bar', '.swal2-timer-progress-bar-container'
   ];
   const selectorString = classesToSearch.map(classSelector => `${classSelector}[style*="display: none"]`).join(',');

   const hiddenElements = magnetDoc.querySelectorAll(selectorString);
   hiddenElements.forEach(el => el?.remove());

   if (data.prompt_type === 'inline') {
      magnetDoc.removeAttribute('tabindex');
   }

   if (previewMode) {
      addPreviewTooltipToButtons(magnetDoc)
   }

   if (settings?.enableCodeSnippets === true) {
      cutAndRunScript(magnetDoc);
   }

   if (conversionTrackingEnabled) {
      const conversionTargets = magnetDoc.querySelectorAll('[data-conversion-target]');
      conversionTargets.forEach(element => {
         element.addEventListener('click', () => {
            Swal.close({isConfirmed: true, targetUrl: element.href});
         });
      });
   }
}

function cutAndRunScript(magnetDoc) {
   const htmlContainer = magnetDoc.querySelector('.swal2-html-container');
   const footer = magnetDoc.querySelector('.swal2-footer');

   if (htmlContainer) {
      htmlContainer.innerHTML = htmlContainer.innerHTML.replaceAll("&lt;", "<").replaceAll("&gt;", ">");
   }

   if (footer) {
      footer.innerHTML = footer.innerHTML.replaceAll("&lt;", "<").replaceAll("&gt;", ">");
   }

   const scripts = [];
   if (htmlContainer) {
      const htmlScripts = Array.from(htmlContainer.getElementsByTagName('script'));
      scripts.push(...htmlScripts);
   }
   if (footer) {
      const footerScripts = Array.from(footer.getElementsByTagName('script'));
      scripts.push(...footerScripts);
   }

   const loadAndExecuteScripts = (scripts) => {
      const promises = scripts.map(script => {
         return new Promise((resolve, reject) => {
            const scriptElement = document.createElement('script');
            if (script.src) {
               scriptElement.src = script.src;
               scriptElement.onload = resolve;
               scriptElement.onerror = reject;
            } else {
               resolve();
            }
            document.head.appendChild(scriptElement);
         });
      });
      return Promise.all(promises);
   };

   const executeScripts = (scripts) => {
      scripts.forEach(script => {
         if (!script.src) {
            try {
               eval(script.innerText);
            } catch (error) {
               console.error('Error executing script:', error);
            }
         }
      });
   };

   // Load scripts asynchronously and wait for all to be loaded
   loadAndExecuteScripts(scripts)
      .then(() => {
         // All scripts loaded, now execute their content
         executeScripts(scripts);
      })
      .catch(error => {
         console.error('Error loading or executing scripts:', error);
      });
}

function injectCustomCss(wrapper, css) {
   if (!wrapper || !css || typeof css !== 'string') {
      return null;
   }
   const scoped = scopeCustomCss(css);
   if (!scoped) {
      return null;
   }
   const styleEl = document.createElement('style');
   styleEl.setAttribute('data-hum-custom-css', 'true');
   styleEl.textContent = scoped;
   wrapper.appendChild(styleEl);
   return styleEl;
}

function addPreviewTooltipToButtons(doc) {
   let swalButtons = doc.querySelectorAll('button');

   swalButtons.forEach((swalButton) => {
      let tooltipDiv = document.createElement('div');
      tooltipDiv.className = 'hum-tooltip hum-tooltip-top';
      tooltipDiv.innerHTML = "You're in preview mode, which is for visual demonstration only. Prompt buttons are not clickable.";

      let wrapDiv = document.createElement('div');
      wrapDiv.className = 'hum-tooltip-container';

      let clonedButton = swalButton.cloneNode(true);
      clonedButton.classList.add('hum-disabled');

      wrapDiv.appendChild(clonedButton);
      wrapDiv.appendChild(tooltipDiv);

      if (swalButton.classList.contains('swal2-close')) {
         let subWrap = document.createElement('div');
         subWrap.style.display = "flex";
         subWrap.style.justifyContent = "end";
         subWrap.appendChild(wrapDiv);

         swalButton.parentNode.replaceChild(subWrap, swalButton);
      } else {
         swalButton.parentNode.replaceChild(wrapDiv, swalButton);
      }
   });
}

// remove function when removing Sc27746
function oldRegexValidateEmail(email) {
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   return emailRegex.test(email);
}

function findAndReplacePrimaryInstitutionName(text = '', primaryInstitutionName) {
   return text.replaceAll('%%primary_institution_name%%', primaryInstitutionName);
}

function convertToSnakeCase(obj) {
   if (typeof obj !== 'object' || obj === null) {
      return obj;
   }

   if (Array.isArray(obj)) {
      return obj.map(convertToSnakeCase);
   }

   return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
         key.replace(/[A-Z]/g, match => `_${match.toLowerCase()}`),
         convertToSnakeCase(value),
      ]),
   );
}

function findMatchingStateContainer(countryContainer) {
   const key = countryContainer.getAttribute('data-key');

   let stateKey = 'state';
   if (key !== 'country') {
      stateKey = key.replace(new RegExp("country$", "ig"), "state");
   }

   return document.querySelector(`.state-select-container[data-key='${stateKey}']`);
}

function addListenerToCountrySelect(countryContainer, stateContainer) {
   countryContainer.addEventListener('change', function (event) {
      const selectedCountry = event.target.value;
      const stateSelectContainer = stateContainer;
      let placeholder = stateSelectContainer.getAttribute('data-placeholder');
      const isRequired = stateSelectContainer.getAttribute('data-isrequired');
      const inputRow = stateSelectContainer.parentElement.closest('.swal-input-row');

      if (selectedCountry === 'US' || selectedCountry === 'CA') {
         const selectedCountryRegions = selectedCountry === 'US' ? statesUS : provincesCanada;
         if (!placeholder) {
            placeholder = selectedCountry === 'US' ? 'U.S. States/Territories' : 'Canadian Provinces';
         }
         let stateSelectHTML = `<option selected disabled value="">${placeholder ?? ''}</option>`;
         selectedCountryRegions.map(({name, code}) => stateSelectHTML += `<option value="${code}">${name}</option>`);

         //make states/provinces visible and update required status
         stateSelectContainer.setAttribute('data-required', isRequired);
         stateSelectContainer.innerHTML = stateSelectHTML;
         Array.from(inputRow.children).forEach(child => {
            child.classList.remove('swal-hidden');
         });
      } else {
         //reset states/provinces visibility, values and requirements
         Array.from(inputRow.children).forEach(child => {
            child.classList.add('swal-hidden');
         });
         stateSelectContainer.value = '';
         stateSelectContainer.setAttribute('data-required', 'false');
      }
   });
}

function addCustomField(body, key, value) {
   const {label, required, slug, placeholder, type, select_options, is_primary, default_value} = value;
   let isCountry;

   switch (type) {
      case 'text':
      case 'email':
         body += `<label for="swal-input${key}" class="swal-input-label">${label}<input data-label="${label}" data-required="${required}" data-key="${slug}" id="swal-input${key}" class="swal2-input swal-input" type="${type}" placeholder="${placeholder ?? ''}"></label>`;
         break;
      case 'checkbox':
         body += `<label for="swal-input${key}" class="swal-input-label swal-checkbox-label"><input data-label="${label.replace(/(<([^>]+)>)/gi, "")}" data-required="${required}" data-key="${slug}" id="swal-input${key}" class="swal2-input swal-input" type="${type}" ${default_value ? 'checked' : ''}>${label}</label>`;
         break;
      case 'select':
         body += `<label for="swal-select${key}" class="swal-input-label">${label}<select id="swal-select${key}" class="swal2-input swal-input swal2-select swal-select" data-required="${required}" data-key="${slug}">`;
         body += `<option selected disabled value="">${placeholder ?? ''}</option>`;
         select_options.map(({label, value}) => body += `<option value="${value}">${label ? label : value}</option>`);
         body += `</select></label>`;
         break;
      case 'location':
         isCountry = slug.endsWith('country');

         if (isCountry) {
            body += `<label for="swal-select${key}" class="swal-input-label">${label}<select id="swal-select${key}" class="swal2-input swal-input swal2-select swal-select country-select-container" data-required="${required}" data-key="${slug}">`;
            body += `<option selected disabled>${placeholder ?? ''}</option>`;
            countryCodes.map(({name, iso}) => body += `<option value="${iso}">${name}</option>`);
            body += `</select></label>`;
            break;
         }

         // States/provinces will be hidden and empty until needed. Construct parent element here since this is the only place where we fill the attributes.
         body += `<label for="swal-select${key}" class="swal-input-label swal-hidden">${label}<select id="swal-select${key}" class="swal2-input swal-input swal2-select swal-select state-select-container" data-key="${slug}" data-label="${label}" data-isrequired="${required}" data-placeholder="${placeholder ?? ''}"></select></label>`;
         break;
   }

   return body;
}

function addDefaultField(body, key, value) {
   const {label, required, slug} = value;

   switch (slug) {
      case 'country':
         body += `<label for="swal-select${key}" class="swal-input-label">${label}<select id="swal-select${key}" class="swal2-input swal-input swal2-select swal-select" data-required="${required}" data-key="${slug}">`;
         body += `<option selected disabled>${label}</option>`;
         countryCodes.map(({name, iso}) => body += `<option value="${iso}">${name}</option>`);
         body += `</select></label>`;
         break;
      default:
         body += `<label for="swal-input${key}" class="swal-input-label">${label}<input data-label="${label}" data-required="${required}" data-key="${slug}" id="swal-input${key}" class="swal2-input swal-input" type="${slug === 'email' ? 'email' : 'text'}" placeholder="${label}"></label>`;
   }

   return body;
}

function findPrimaryEmail(data, tracker) {
   const activeCampaign = tracker?.campaigns?.campaigns?.find((campaign) => campaign.code === data.form_id);

   if (activeCampaign) {
      const activeStage = activeCampaign?.stages?.find((stage) => stage.id === data.stage_id);

      if (activeStage) {
         const activeTouchpoint = activeStage?.touchpoints?.find((touchpoint) => touchpoint.id === data.touchpoint_id);

         if (activeTouchpoint) {
            const activeVariant = activeTouchpoint?.variants?.find((variant) => variant.variant_id === data.variant_id);

               const isPrimaryField = activeVariant?.form_data?.inputs?.find((input) => !!input?.is_primary);
               const isPrimaryKey = isPrimaryField?.target_property || isPrimaryField?.slug;

               if (isPrimaryKey) return data[isPrimaryKey];
         }
      }
   }

   for (const key in data) {
      if (key.endsWith('email')) {
         return data[key];
      }
   }

   return null;
}
